import React, { useState } from 'react';
import { notification, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { InfoMessage, LabelListDisplay, SeverityTag } from 'components';
import {
  Input,
  Icon,
  Title,
  Body,
  Tooltip,
  Button,
  Flex,
  Chip,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import {
  Milestone,
  Rule,
  RuleRunStatus,
  RuleSeverity,
  RunStatus,
} from 'types/dt-graphql-apollo-v2';
import { formatDateRecentTime, sortStringOrUndefined } from 'utils/misc';
import { compareRunDates } from 'utils/data-validation';
import { PAGINATION_PAGE_SIZE } from 'utils/config';
import { RuleRunValidity } from 'pages/ViewDigitalTwinPage/components/Schemas/ViewSchemaPage/DataValidation/ViewRunsPage/RunsHistory';
import { isFailedRun } from 'utils/runStatistics';
import { DownloadReportOptions, useDownloadReport } from './useDownloadReport';
import { useExportReportToDM } from './useExportReportToDM';

export const CollectionRulesTable = ({
  collection,
  ruleRuns,
  selectRule,
}: {
  collection: Milestone;
  ruleRuns: RuleRunStatus[];
  selectRule: (rule: Rule) => void;
}) => {
  const [search, setSearch] = useState<string>('');

  const { downloadReport, downloadingReport } = useDownloadReport();
  const { exportReportToDM: doExportReportToDM, exportingReport } =
    useExportReportToDM();

  // The filtered report is not supported yet to be downloaded.
  const collectionIsFiltered = ruleRuns?.some((run) =>
    run?.reportId?.includes('-filter')
  );
  const filterSearch = (searchValue: string) => {
    if (!searchValue || !ruleRuns) return ruleRuns;
    const searchTerm = searchValue.trim().toLowerCase();
    return ruleRuns.filter(({ rule }) => {
      const searchTargets = [
        rule?.name,
        rule?.customerDefinedId,
        rule?.errorMessage,
      ];
      return searchTargets.some(
        (searchTarget) =>
          searchTarget && searchTarget?.toLowerCase().includes(searchTerm)
      );
    });
  };

  const ruleRunsAfterSearch = filterSearch(search).map(isFailedRun);

  const statusCounts = ruleRunsAfterSearch.reduce(
    (acc: { [key: string]: number }, obj) => {
      acc[obj.status] = (acc[obj.status] || 0) + 1;
      return acc;
    },
    { [RunStatus.Success]: 0, [RunStatus.Error]: 0, [RunStatus.InProgress]: 0 }
  );

  const totalFailedCount =
    ruleRunsAfterSearch?.reduce((acc = 0, cur: RuleRunStatus | null) => {
      const totalFailed = cur?.totalFailed || 0;
      const succeeded = cur?.status === RunStatus.Success;

      return succeeded ? acc + totalFailed : acc;
    }, 0) || 0;

  const reportAvailable =
    statusCounts.ERROR === 0 && statusCounts.IN_PROGRESS === 0;

  const downloadReportTooltip = () => {
    if (reportAvailable) return 'Download the xlsx file.';
    if (statusCounts.IN_PROGRESS > 0)
      return `Incomplete report. ${statusCounts.IN_PROGRESS} jobs in progress.`;
    if (statusCounts.ERROR > 0)
      return `Incomplete report. ${statusCounts.ERROR} jobs failed.`;
    return '';
  };

  const columns: ColumnsType<RuleRunStatus> = [
    {
      title: 'Last updated',
      key: 'runAt',
      width: '15%',
      sorter: (a, b) => compareRunDates(a, b),
      render: (record: RuleRunStatus) => (
        <>
          {record.status === RunStatus.InProgress &&
            formatDateRecentTime(record.runAt || null)}
          {record.status === RunStatus.Error && (
            <Chip
              type="danger"
              label={`Failed: ${formatDateRecentTime(record.runAt || null)}`}
              size="small"
            />
          )}
          {record.status === RunStatus.Success &&
            formatDateRecentTime(record.runAt || null)}
        </>
      ),
    },
    {
      title: 'Rule number',
      key: 'customerDefinedId',
      width: '20%',
      sorter: (a, b) =>
        sortStringOrUndefined(
          a.rule?.customerDefinedId,
          b.rule?.customerDefinedId
        ),
      render: (record: RuleRunStatus) =>
        record.rule?.customerDefinedId || (
          <EmptyRowPlaceholder>No rule number was set</EmptyRowPlaceholder>
        ),
    },
    {
      title: 'Rule name',
      key: 'name',
      width: '30%',
      sorter: (a, b) => sortStringOrUndefined(a.rule?.name, b.rule?.name),
      render: (record: RuleRunStatus) => (
        <Tooltip
          content={
            <Body level={6}>{`Error message: ${
              record.rule?.errorMessage || '-'
            }`}</Body>
          }
        >
          <span>{record.rule?.name}</span>
        </Tooltip>
      ),
    },
    {
      title: 'No. failed items',
      key: 'failedItemsCount',
      width: '15%',
      sorter: (a, b) => (a.totalFailed || 0) - (b.totalFailed || 0),
      render: (record: RuleRunStatus) => (
        <RuleRunValidity runStatus={record} showPercentage={false} />
      ),
    },
    {
      title: 'Severity',
      key: 'severity',
      width: '10%',
      sorter: (a, b) =>
        sortStringOrUndefined(a.rule?.severity, b.rule?.severity),
      render: (record: RuleRunStatus) =>
        record.rule?.severity && (
          <SeverityTag severity={record.rule?.severity as RuleSeverity} />
        ),
    },
    {
      title: 'Labels',
      key: 'labels',
      width: '10%',
      sorter: (a, b) => {
        const sortedATags = a.rule?.tags?.slice().sort(sortStringOrUndefined);
        const sortedBTags = b.rule?.tags?.slice().sort(sortStringOrUndefined);
        return sortStringOrUndefined(sortedATags?.[0], sortedBTags?.[0]);
      },
      render: (record: RuleRunStatus) => (
        <LabelListDisplay
          labels={record.rule?.tags?.map((label) => ({
            text: label,
            key: label,
          }))}
        />
      ),
    },
    {
      key: 'icon',
      render: () => (
        <Icon style={{ verticalAlign: 'middle' }} type="ArrowRight" />
      ),
    },
  ];

  const renderDownloadReportButton = () => {
    const options: DownloadReportOptions = {
      collection,
      ruleRuns,
      onError: (err) => {
        notification.error({
          message: 'Could not download the report!',
          description: err.toString(),
        });
      },
      onSuccess: (desc) => {
        notification.success({
          message: 'Error Report was downloaded!',
          description: desc,
        });
      },
    };

    return (
      <>
        <Tooltip content={downloadReportTooltip()}>
          <Button
            disabled={
              !reportAvailable || downloadingReport || totalFailedCount === 0
            }
            loading={downloadingReport}
            icon="Download"
            onClick={() => downloadReport(options)}
          >
            Download result
          </Button>
        </Tooltip>
        <Tooltip content="Export failed instances to CDF data model">
          <Button
            type="tertiary"
            disabled={
              !reportAvailable || exportingReport || totalFailedCount === 0
            }
            loading={exportingReport}
            icon="Upload"
            onClick={() => doExportReportToDM({ ruleRuns })}
          >
            Export Report
          </Button>
        </Tooltip>
      </>
    );
  };

  const renderTable = () => (
    <>
      <Flex direction="row" justifyContent="space-between" alignItems="center">
        <StyledBody>
          <b>{statusCounts[RunStatus.Error]}</b>&nbsp;Failed runs,
          Validated&nbsp;
          <b>{statusCounts[RunStatus.Success]}</b>&nbsp; rules, including&nbsp;
          <b>{totalFailedCount}</b> violating items
        </StyledBody>
        <Flex direction="row" gap={10}>
          {!collectionIsFiltered && renderDownloadReportButton()}

          <StyledInput
            placeholder="Search…"
            icon="Search"
            iconPlacement="left"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Flex>
      </Flex>

      <StyledTable>
        <Table
          rowKey="id"
          dataSource={ruleRunsAfterSearch || []}
          columns={columns}
          onRow={(record) => ({
            onClick: () => {
              selectRule(record.rule as Rule);
            },
          })}
          pagination={{
            defaultPageSize: PAGINATION_PAGE_SIZE,
            hideOnSinglePage: true,
          }}
        />
      </StyledTable>
    </>
  );

  const renderContent = () => {
    if (!ruleRuns)
      return <InfoMessage type="Error" title="No data available" />;

    return renderTable();
  };

  return (
    <StyledContainer>
      <Title level={4}>Rules</Title>
      {renderContent()}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  margin: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
`;

const StyledBody = styled(Body)`
  font-size: 0.875rem;
  line-height: 1.25rem;
`;

const StyledInput = styled(Input)`
  .cogs-input {
    border-radius: 6px;
  }
  .cogs-icon-Search {
    opacity: 0.25;
    left: 12px;
  }
  .input-clear-button {
    top: calc(50% - 28px / 2);
    right: 2px !important;
    width: 28px;
    height: 28px;
    .cogs-icon.input-content.input-icon.cogs-icon-Close {
      top: calc(50% - 12px / 2);
      width: 12px !important;
      height: 12px !important;
    }
  }
`;

const StyledTable = styled.div`
  .ant-table {
    background: transparent;
  }
  table {
    border-spacing: 0 4px;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table tfoot > tr > th,
  .ant-table tfoot > tr > td {
    padding: 10px 16px;
  }
  .ant-table-thead {
    > tr > th {
      border-bottom: none;
      background: transparent;
    }
  }
  .ant-table-row {
    background: var(--cogs-white);
    box-shadow: var(--cogs-z-4);
    cursor: pointer;
  }
  .anticon.ant-table-column-sorter-up,
  .anticon.ant-table-column-sorter-down {
    &.active {
      color: var(--cogs-primary);
    }
  }
`;

const EmptyRowPlaceholder = styled.i`
  color: var(--cogs-greyscale-grey5);
`;
